"use client"

import { useState } from "react"
import { useRouter } from "next/navigation"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { ArrowRight, Building2, ChartNoAxesColumn, Eye, EyeOff, ShieldCheck } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { loginSchema, type LoginInput } from "@kash/shared"
import { useAuthStore } from "@/store/auth.store"
import { api } from "@/lib/api"

const highlights = [
  {
    icon: Building2,
    label: "Control de Caja",
    description: "Gestión centralizada de movimientos y soportes operativos.",
  },
  {
    icon: ChartNoAxesColumn,
    label: "Trazabilidad",
    description: "Seguimiento en tiempo real de ingresos, egresos y aprobaciones.",
  },
  {
    icon: ShieldCheck,
    label: "Seguridad Corporativa",
    description: "Acceso autenticado con auditoría completa de cada acción.",
  },
]

export function LoginForm() {
  const { push } = useRouter()
  const setSession = useAuthStore((s) => s.setSession)
  const [serverError, setServerError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  })

  async function onSubmit(values: LoginInput) {
    setServerError(null)

    try {
      const { data } = await api.post("/api/v1/auth/login", values)
      const user = data.data.user
      const refreshToken = data.data.refreshToken

      if (user.mustChangePassword) {
        setSession({ ...user, mustChangePassword: true }, data.data.accessToken, refreshToken)
        push("/dashboard/mi-cuenta/password")
        return
      }

      setSession(user, data.data.accessToken, refreshToken)
      window.location.href = "/dashboard"
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error ?? err.message
          : "Error al iniciar sesión"

      setServerError(msg)
    }
  }

  return (
    <div className="grid h-full w-full overflow-hidden lg:grid-cols-[1.12fr_0.88fr]">
      <section className="relative isolate overflow-hidden bg-[linear-gradient(160deg,#fff8f4_0%,#ffffff_46%,#f7f7f7_100%)] dark:bg-[linear-gradient(160deg,#1a1a1a_0%,#0a0a0a_46%,#000000_100%)] px-6 py-6 sm:px-8 sm:py-8 lg:h-full lg:px-10 lg:py-10">
        <div className="absolute inset-x-0 top-0 h-40 bg-[radial-gradient(circle_at_top_left,rgba(255,56,92,0.2),transparent_58%)] dark:bg-[radial-gradient(circle_at_top_left,rgba(255,56,92,0.1),transparent_58%)]" />
        <div className="absolute -left-16 top-28 size-40 rounded-full bg-[rgba(255,56,92,0.12)] dark:bg-[rgba(255,56,92,0.05)] blur-3xl" />
        <div className="absolute bottom-10 right-0 size-56 rounded-full bg-[rgba(146,23,77,0.09)] dark:bg-[rgba(146,23,77,0.04)] blur-3xl" />

        <div className="relative flex h-full flex-col">
          <div className="flex-none">
            <div className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-background/90 px-3 py-2 shadow-sm backdrop-blur">
              <div className="flex size-10 items-center justify-center rounded-full bg-[linear-gradient(135deg,#ff385c_0%,#e00b41_65%,#92174d_100%)] text-sm font-extrabold text-white">
                K
              </div>
              <div>
                <p className="text-sm font-bold text-foreground">Kash</p>
                <p className="text-xs text-muted-foreground">Tesoreria de Partequipos</p>
              </div>
            </div>
          </div>

          <div className="flex flex-1 flex-col justify-center">
            <div className="max-w-xl">
              <h1 className="max-w-lg text-[2.45rem] font-extrabold leading-[1.02] tracking-[-0.04em] text-foreground sm:text-[3.2rem]">
                Gestión inteligente de tesorería y caja.
              </h1>
              <p className="mt-5 max-w-lg text-[15px] leading-7 text-muted-foreground sm:text-base">
                Plataforma interna para el control operativo de Partequipos. 
                Optimiza tus flujos de caja con visibilidad total y seguridad integrada.
              </p>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {highlights.map(({ icon: Icon, label, description }) => (
                <div
                  key={label}
                  className="rounded-[22px] border border-border bg-background/90 p-5 shadow-[0_10px_25px_rgba(0,0,0,0.04)] backdrop-blur transition-all hover:shadow-[0_12px_30px_rgba(0,0,0,0.08)]"
                >
                  <div className="flex size-12 items-center justify-center rounded-full bg-muted text-foreground">
                    <Icon className="size-5" />
                  </div>
                  <p className="mt-5 text-[15px] font-bold text-foreground">{label}</p>
                  <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="flex h-full items-center bg-background px-6 py-6 sm:px-8 sm:py-8 lg:px-10">
        <div className="mx-auto w-full max-w-md">
          <div className="p-2 sm:p-4">
            <div className="mb-6">
              <p className="text-sm font-semibold text-muted-foreground">Bienvenido de nuevo</p>
              <h2 className="mt-2 text-[2rem] font-extrabold leading-tight tracking-[-0.03em] text-foreground">
                Ingresar
              </h2>
              <p className="mt-3 text-sm leading-6 text-muted-foreground">
                Accede con tus credenciales corporativas.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} method="POST" className="space-y-5">
              {serverError && (
                <p className="rounded-2xl border border-[rgba(193,53,21,0.18)] bg-[rgba(193,53,21,0.08)] px-4 py-3 text-sm text-destructive">
                  {serverError}
                </p>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-[13px] font-bold text-foreground">
                  Correo electrónico
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="usuario@partequipos.com"
                  autoComplete="email"
                  className="h-12 rounded-2xl border-border px-4 text-[15px] text-foreground placeholder:text-muted-foreground focus-visible:border-foreground focus-visible:ring-2 focus-visible:ring-foreground"
                  {...register("email")}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <Label htmlFor="password" className="text-[13px] font-bold text-foreground">
                    Contraseña
                  </Label>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    className="h-12 rounded-2xl border-border px-4 pr-12 text-[15px] text-foreground placeholder:text-muted-foreground focus-visible:border-foreground focus-visible:ring-2 focus-visible:ring-foreground"
                    {...register("password")}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((prev) => !prev)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    tabIndex={-1}
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                  >
                    {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive">{errors.password.message}</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="h-12 w-full rounded-2xl bg-[linear-gradient(90deg,#ff385c_0%,#e00b41_55%,#92174d_100%)] text-[15px] font-bold text-white shadow-[0_12px_24px_rgba(224,11,65,0.24)] transition-transform hover:scale-[0.99] hover:opacity-95"
              >
                {isSubmitting ? "Ingresando…" : "Entrar al dashboard"}
                {!isSubmitting && <ArrowRight className="ml-2 size-4" />}
              </Button>
            </form>


          </div>

          <p className="mt-5 text-center text-xs text-muted-foreground" suppressHydrationWarning>
            © {new Date().getFullYear()} Partequipos. Todos los derechos reservados.
          </p>
        </div>
      </section>
    </div>
  )
}
